import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  EmojiEvents,
  AccountTree,
  TrackChanges,
  Star,
  Timeline,
  Add,
} from '@mui/icons-material';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { healthWorthService } from '../services/supabaseService';
// Removed talentTreeService import - now using weekly lock-in progress instead
import { weeklyLockInService, WeeklyLockingWithTalents, ProgressSummary } from '../services/weeklyLockInService';
import OnboardingTour from '../components/OnboardingTour';
import OnboardingProgress from '../components/OnboardingProgress';
import { onboardingService, OnboardingProgress as OnboardingProgressType } from '../services/onboardingService';

const MotionCard = motion(Card);

// Compact Overall Progress Card for Dashboard
interface CompactProgressCardProps {
  percentage: number;
  completedMinutes: number;
  totalMinutes: number;
  hasActiveLocking: boolean;
}

const CompactProgressCard: React.FC<CompactProgressCardProps> = ({
  percentage,
  completedMinutes,
  totalMinutes,
  hasActiveLocking,
}) => {
  const getGradientColors = (percentage: number): [string, string] => {
    if (percentage >= 80) return ['#10b981', '#059669'];
    if (percentage >= 60) return ['#f59e0b', '#d97706'];
    if (percentage >= 40) return ['#f97316', '#ea580c'];
    return ['#ef4444', '#dc2626'];
  };

  // If no active locking, show a different state
  if (!hasActiveLocking) {
    return (
      <Card 
        sx={{
          position: 'relative',
          height: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.1)',
            zIndex: 0,
          }}
        />
        
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                width: 40,
                height: 40,
              }}
            >
              <Schedule sx={{ color: 'white', fontSize: '1.2rem' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}>
                Weekly Progress
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem' }}>
                No active lock-in
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: 'white',
                fontSize: '1.8rem',
                mb: 1,
              }}
            >
              --
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Create a weekly lock-in to track progress
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const [primaryColor, secondaryColor] = getGradientColors(percentage);

  return (
    <Card 
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              width: 40,
              height: 40,
            }}
          >
            <TrendingUp sx={{ color: 'white', fontSize: '1.2rem' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}>
              Weekly Progress
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem' }}>
              Overall completion status
            </Typography>
          </Box>
        </Box>
        
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                color: 'white',
                fontSize: '2rem',
              }}
            >
              {percentage.toFixed(0)}%
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem' }}>
              {completedMinutes}/{totalMinutes} min
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

interface DashboardStats {
  lastHealthWorth: number | null;
  healthWorthChange: number | null;
  healthWorthTrend: 'up' | 'down' | 'neutral' | null;
}

interface RecentActivity {
  id: string;
  type: 'health_assessment' | 'weekly_progress_report' | 'health_worth_change' | 'health_tree_save' | 'weekly_lockin_created' | 'weekly_lockin_expired' | 'talent_submission';
  title: string;
  description: string;
  timestamp: Date;
  value?: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<DashboardStats>({
    lastHealthWorth: null,
    healthWorthChange: null,
    healthWorthTrend: null,
  });

  // Removed timeStats as it's no longer needed - using weekly lock-in progress instead

  const [lastWeekProgress, setLastWeekProgress] = useState<{
    locking: WeeklyLockingWithTalents | null;
    progressSummary: ProgressSummary[];
  }>({
    locking: null,
    progressSummary: [],
  });

  const [currentWeekProgress, setCurrentWeekProgress] = useState<{
    locking: WeeklyLockingWithTalents | null;
    progressSummary: ProgressSummary[];
    percentage: number;
    completedMinutes: number;
    totalMinutes: number;
  }>({
    locking: null,
    progressSummary: [],
    percentage: 0,
    completedMinutes: 0,
    totalMinutes: 0,
  });

  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);

  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Onboarding state
  const [showTour, setShowTour] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgressType>({
    has_visited_health_tree: false,
    has_created_lock_in: false,
    has_reported_progress: false,
    tour_completed: false,
    last_updated: new Date().toISOString(),
  });
  const [showProgressWidget, setShowProgressWidget] = useState(false);

  // Load latest health worth assessment and calculate change
  useEffect(() => {
    const loadHealthWorthData = async () => {
      if (!user?.id) return;
      
      try {
        const assessments = await healthWorthService.getHealthAssessments(user.id);
        if (assessments.length > 0) {
          const latestValue = assessments[0].future_earning_capacity;
          let changeValue: number | null = null;
          let trendDirection: 'up' | 'down' | 'neutral' | null = null;

          // Calculate change if we have at least 2 assessments
          if (assessments.length > 1) {
            const previousValue = assessments[1].future_earning_capacity;
            changeValue = latestValue - previousValue;
            
            if (changeValue > 0) {
              trendDirection = 'up';
            } else if (changeValue < 0) {
              trendDirection = 'down';
            } else {
              trendDirection = 'neutral';
            }
          }

          setStats(prev => ({
            ...prev,
            lastHealthWorth: latestValue,
            healthWorthChange: changeValue,
            healthWorthTrend: trendDirection
          }));
        }
      } catch (error) {
        console.error('Error loading health worth:', error);
      }
    };

    loadHealthWorthData();
  }, [user?.id]);

  // Load onboarding progress and check if tour should be shown
  useEffect(() => {
    const loadOnboardingData = async () => {
      if (!user?.id) return;
      
      try {
        // Get current onboarding progress
        const progress = await onboardingService.getOnboardingProgress(user.id);
        setOnboardingProgress(progress);
        
        // Check if tour should be shown
        const shouldShowTour = await onboardingService.shouldShowTour(user.id);
        if (shouldShowTour && !onboardingService.isTourActive(user.id)) {
          // First time showing tour - mark it as active
          onboardingService.markTourActive(user.id);
        }
        setShowTour(shouldShowTour);
        
        // Check if progress widget should be shown
        const shouldShowWidget = await onboardingService.shouldShowProgressWidget(user.id);
        setShowProgressWidget(shouldShowWidget);
      } catch (error) {
        console.error('Error loading onboarding data:', error);
      }
    };

    loadOnboardingData();
  }, [user?.id]);

  // Removed talent tree time stats - now using weekly lock-in progress tracking instead

  // Load last week's progress data
  useEffect(() => {
    const loadLastWeekProgress = async () => {
      if (!user?.id) return;
      
      try {
        // Get user's locking history to find last week's completed lock-in
        const lockingHistory = await weeklyLockInService.getLockingHistory(user.id, 10);
        
        // Find the most recent completed lock-in (not current week)
        const completedLockings = lockingHistory.filter(locking => 
          locking.status === 'completed' || locking.status === 'expired'
        );
        
        if (completedLockings.length > 0) {
          const lastWeekLocking = completedLockings[0];
          const progressSummary = await weeklyLockInService.getProgressSummary(lastWeekLocking.id);
          
          setLastWeekProgress({
            locking: lastWeekLocking,
            progressSummary,
          });
        }
      } catch (error) {
        console.error('Error loading last week progress:', error);
      }
    };

    loadLastWeekProgress();
  }, [user?.id]);

  // Load current week's progress data
  useEffect(() => {
    const loadCurrentWeekProgress = async () => {
      if (!user?.id) return;
      
      try {
        // Get current active lock-in
        const currentLocking = await weeklyLockInService.getCurrentLocking(user.id);
        
        if (currentLocking) {
          // Get progress summary for current lock-in
          const progressSummary = await weeklyLockInService.getProgressSummary(currentLocking.id);
          
          // Calculate progress metrics (same logic as in WeeklyLockInDashboard)
          const totalTarget = currentLocking.total_time_minutes;
          const totalCompleted = progressSummary.reduce((sum, talent) => sum + talent.completed_minutes, 0);
          const percentage = totalTarget > 0 ? Math.min((totalCompleted / totalTarget) * 100, 100) : 0;
          
          setCurrentWeekProgress({
            locking: currentLocking,
            progressSummary,
            percentage,
            completedMinutes: totalCompleted,
            totalMinutes: totalTarget,
          });
        } else {
          // No active lock-in
          setCurrentWeekProgress({
            locking: null,
            progressSummary: [],
            percentage: 0,
            completedMinutes: 0,
            totalMinutes: 0,
          });
        }
      } catch (error) {
        console.error('Error loading current week progress:', error);
      }
    };

    loadCurrentWeekProgress();
  }, [user?.id]);

  // Load recent activity data
  useEffect(() => {
    const loadRecentActivity = async () => {
      if (!user?.id) return;
      
      try {
        const activities: RecentActivity[] = [];
        
        // Load weekly lock-in history (created and expired)
        const lockingHistory = await weeklyLockInService.getLockingHistory(user.id, 5);
        
        for (const locking of lockingHistory) {
          // Add lock-in creation activity
          activities.push({
            id: `lockin-created-${locking.id}`,
            type: 'weekly_lockin_created',
            title: 'Weekly Commitment Created',
            description: `Locked in ${locking.locked_talents?.length || 0} talents for the week`,
            timestamp: new Date(locking.created_at),
            value: `${locking.total_time_minutes} min/week`,
          });
          
          // Add lock-in expiration activity if it has expired
          const expiresAt = new Date(locking.expires_at);
          const now = new Date();
          if (expiresAt < now && (locking.status === 'expired' || locking.status === 'completed')) {
            activities.push({
              id: `lockin-expired-${locking.id}`,
              type: 'weekly_lockin_expired',
              title: 'Weekly Commitment Expired',
              description: `Weekly commitment period ended - ${locking.status === 'completed' ? 'goals achieved' : 'time expired'}`,
              timestamp: expiresAt,
              value: locking.status === 'completed' ? 'Completed' : 'Expired',
            });
          }
          
          // Load talent submissions (progress entries) for this locking
          try {
            const progressEntries = await weeklyLockInService.getProgress(locking.id);
            
            // Group by date to avoid too many individual entries
            const entriesByDate = new Map<string, { talents: string[], totalMinutes: number, date: Date }>();
            
            for (const entry of progressEntries) {
              const date = entry.entry_date;
              if (!entriesByDate.has(date)) {
                entriesByDate.set(date, {
                  talents: [],
                  totalMinutes: 0,
                  date: new Date(entry.created_at),
                });
              }
              
              const dayData = entriesByDate.get(date)!;
              const talentName = locking.locked_talents?.find(t => t.talent_id === entry.talent_id)?.talent_name || entry.talent_id;
              dayData.talents.push(talentName);
              dayData.totalMinutes += entry.minutes_completed;
            }
            
            // Add activity for each day with submissions (limit to recent ones)
            const recentEntries = Array.from(entriesByDate.values())
              .sort((a, b) => b.date.getTime() - a.date.getTime())
              .slice(0, 3); // Only show 3 most recent days per locking
            
            for (const dayData of recentEntries) {
              activities.push({
                id: `talent-submission-${locking.id}-${dayData.date.toISOString().split('T')[0]}`,
                type: 'talent_submission',
                title: 'Talent Progress Reported',
                description: `Reported progress for ${dayData.talents.length} talent${dayData.talents.length > 1 ? 's' : ''}`,
                timestamp: dayData.date,
                value: `${dayData.totalMinutes} min`,
              });
            }
          } catch (error) {
            console.error('Error loading progress entries for locking:', locking.id, error);
          }
        }
        
        // Load health assessments
        try {
          const assessments = await healthWorthService.getHealthAssessments(user.id);
          for (let i = 0; i < Math.min(assessments.length, 3); i++) {
            const assessment = assessments[i];
            activities.push({
              id: `assessment-${assessment.id}`,
              type: 'health_assessment',
              title: 'Health Worth Assessment Completed',
              description: 'Recorded new cardiovascular and metabolic health metrics',
              timestamp: new Date(assessment.created_at),
              value: formatHealthWorth(assessment.future_earning_capacity),
            });
            
            // Add health worth change if we have a previous assessment
            if (i < assessments.length - 1) {
              const previousAssessment = assessments[i + 1];
              const change = assessment.future_earning_capacity - previousAssessment.future_earning_capacity;
              if (change !== 0) {
                activities.push({
                  id: `change-${assessment.id}`,
                  type: 'health_worth_change',
                  title: change > 0 ? 'Health Worth Increased' : 'Health Worth Decreased',
                  description: 'Future earning capacity changed from last assessment',
                  timestamp: new Date(assessment.created_at),
                  value: `${change > 0 ? '+' : ''}${formatHealthWorth(change)}`,
                });
              }
            }
          }
        } catch (error) {
          console.error('Error loading health assessments for recent activity:', error);
        }
        
        // Sort all activities by timestamp (most recent first) and take top 6
        const sortedActivities = activities
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
          .slice(0, 6);
        
        setRecentActivity(sortedActivities);
      } catch (error) {
        console.error('Error loading recent activity:', error);
      }
    };

    loadRecentActivity();
  }, [user?.id]);

  // Check for payment success parameter
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      setShowPaymentSuccess(true);
      // Clean up the URL by removing the payment parameter
      searchParams.delete('payment');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Calculate a simple display format for health worth
  const formatHealthWorth = (value: number | null) => {
    if (!value) return 'Not Available';
    return `$${(value / 1000000).toFixed(1)}M`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'health_assessment': return <Timeline sx={{ color: 'success.main' }} />;
      case 'health_worth_change': return <TrendingUp sx={{ color: 'primary.main' }} />;
      case 'weekly_progress_report': return <EmojiEvents sx={{ color: 'warning.main' }} />;
      case 'health_tree_save': return <AccountTree sx={{ color: 'info.main' }} />;
      case 'weekly_lockin_created': return <TrackChanges sx={{ color: 'success.main' }} />;
      case 'weekly_lockin_expired': return <Schedule sx={{ color: 'warning.main' }} />;
      case 'talent_submission': return <Add sx={{ color: 'success.main' }} />;
      default: return <Star sx={{ color: 'grey.500' }} />;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  // Onboarding handlers
  const handleTourComplete = async () => {
    if (user?.id) {
      try {
        await onboardingService.markTourCompleted(user.id);
        onboardingService.clearTourActive(user.id);
        setShowTour(false);
        setOnboardingProgress(prev => ({ ...prev, tour_completed: true }));
      } catch (error) {
        console.error('Error marking tour as completed:', error);
        // Still update the UI state even if the API call fails
        setShowTour(false);
        setOnboardingProgress(prev => ({ ...prev, tour_completed: true }));
      }
    }
  };

  const handleStartTour = () => {
    if (user?.id) {
      onboardingService.markTourActive(user.id);
    }
    setShowTour(true);
  };

  const handleTourClose = () => {
    if (user?.id) {
      onboardingService.clearTourActive(user.id);
    }
    setShowTour(false);
  };

  // Temporary reset function for testing
  const handleResetOnboarding = async () => {
    if (user?.id) {
      onboardingService.resetProgress(user.id);
      // Reload onboarding data
      try {
        const progress = await onboardingService.getOnboardingProgress(user.id);
        setOnboardingProgress(progress);
        
        const shouldShowTour = await onboardingService.shouldShowTour(user.id);
        if (shouldShowTour && !onboardingService.isTourActive(user.id)) {
          onboardingService.markTourActive(user.id);
        }
        setShowTour(shouldShowTour);
        
        const shouldShowWidget = await onboardingService.shouldShowProgressWidget(user.id);
        setShowProgressWidget(shouldShowWidget);
      } catch (error) {
        console.error('Error reloading onboarding data:', error);
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Welcome back, {user?.name}! 👋
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Ready to level up your personal development?
        </Typography>
        
        {/* Temporary reset button for testing specific user */}
        {user?.email === 'anisakaragjozi@yahoo.com' && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleResetOnboarding}
              size="small"
              sx={{ mr: 2 }}
            >
              🔄 Reset Onboarding (Test)
            </Button>
            <Typography variant="caption" color="text.secondary">
              This button resets your onboarding progress so you can experience the tour again.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Onboarding Progress Widget */}
      {showProgressWidget && (
        <Box sx={{ mb: 4 }}>
          <OnboardingProgress
            hasVisitedHealthTree={onboardingProgress.has_visited_health_tree}
            hasCreatedLockIn={onboardingProgress.has_created_lock_in}
            hasReportedProgress={onboardingProgress.has_reported_progress}
            onStartTour={handleStartTour}
          />
        </Box>
      )}

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{ height: '100%' }}
          >
            <CompactProgressCard
              percentage={currentWeekProgress.percentage}
              completedMinutes={currentWeekProgress.completedMinutes}
              totalMinutes={currentWeekProgress.totalMinutes}
              hasActiveLocking={currentWeekProgress.locking !== null}
            />
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            sx={{ height: '100%' }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <Timeline sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatHealthWorth(stats.lastHealthWorth)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest Health Worth
              </Typography>
              {stats.lastHealthWorth && (
                <Chip
                  label="Assessment Complete"
                  size="small"
                  sx={{ mt: 2, backgroundColor: 'success.main', color: 'white' }}
                />
              )}
              {!stats.lastHealthWorth && (
                <Chip
                  label="No Assessment Yet"
                  size="small"
                  sx={{ mt: 2, backgroundColor: 'grey.400', color: 'white' }}
                />
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            sx={{ height: '100%' }}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ 
                bgcolor: stats.healthWorthTrend === 'up' ? 'success.main' : 
                        stats.healthWorthTrend === 'down' ? 'error.main' : 
                        stats.healthWorthTrend === 'neutral' ? 'warning.main' : 'grey.400',
                mx: 'auto', 
                mb: 2, 
                width: 56, 
                height: 56 
              }}>
                <TrackChanges sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold', 
                mb: 1,
                color: stats.healthWorthTrend === 'up' ? 'success.main' : 
                       stats.healthWorthTrend === 'down' ? 'error.main' : 
                       'text.primary'
              }}>
                {stats.healthWorthChange !== null ? 
                  `${stats.healthWorthChange > 0 ? '+' : ''}${formatHealthWorth(stats.healthWorthChange)}` : 
                  'N/A'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Health Worth Change
              </Typography>
              {stats.healthWorthChange !== null && (
                <Chip
                  label={
                    stats.healthWorthTrend === 'up' ? 'Improving' :
                    stats.healthWorthTrend === 'down' ? 'Declining' :
                    'No Change'
                  }
                  size="small"
                  sx={{ 
                    mt: 2,
                    backgroundColor: stats.healthWorthTrend === 'up' ? 'success.main' : 
                                   stats.healthWorthTrend === 'down' ? 'error.main' : 
                                   'warning.main',
                    color: 'white'
                  }}
                />
              )}
              {stats.healthWorthChange === null && (
                <Chip
                  label="Need 2+ Assessments"
                  size="small"
                  sx={{ mt: 2, backgroundColor: 'grey.400', color: 'white' }}
                />
              )}
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <MotionCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<AccountTree />}
                    component={Link}
                    to="/talent-tree"
                    sx={{ py: 2 }}
                  >
                    Talent Trees
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<Timeline />}
                    component={Link}
                    to="/health-worth"
                    sx={{ py: 2 }}
                  >
                    Health Worth
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<TrackChanges />}
                    component={Link}
                    to="/weekly-lockin"
                    sx={{ 
                      py: 2,
                      backgroundColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.dark' }
                    }}
                  >
                    Lock-in Talents
                  </Button>
                </Grid>
              </Grid>

              {!isPremium && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 215, 0, 0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    🌟 Upgrade to Premium for unlimited goals, advanced analytics, and more!
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    component={Link}
                    to="/pricing"
                    sx={{ backgroundColor: 'primary.main', color: 'black' }}
                  >
                    Upgrade Now
                  </Button>
                </Box>
              )}
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <MotionCard
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Recent Activity
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to="/activity-log"
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.light',
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                >
                  View All
                </Button>
              </Box>
              <List>
                {recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        {getActivityIcon(activity.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {activity.title}
                            </Typography>
                            {activity.value && (
                              <Chip
                                label={activity.value}
                                size="small"
                                sx={{ backgroundColor: 'primary.main', color: 'black', fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.secondary" display="block">
                              {activity.description}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                              {formatTimeAgo(activity.timestamp)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </MotionCard>
        </Grid>

        {/* Last Week's Progress - Only show if we have data */}
        {lastWeekProgress.locking && lastWeekProgress.progressSummary.length > 0 && (
          <Grid item xs={12}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                  Last Week's Progress
                  <Chip
                    label={`${new Date(lastWeekProgress.locking.week_start_date).toLocaleDateString()} - ${new Date(lastWeekProgress.locking.week_end_date).toLocaleDateString()}`}
                    size="small"
                    sx={{ ml: 2, backgroundColor: 'primary.main', color: 'black' }}
                  />
                </Typography>
                
                {/* Overall completion */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {lastWeekProgress.locking.completion_percentage.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Completion
                  </Typography>
                </Box>

                {/* Progress by tree type */}
                <Grid container spacing={3}>
                  {['longevity', 'healthspan', 'wellness'].map((treeType) => {
                    const treeTalents = lastWeekProgress.progressSummary.filter(
                      talent => lastWeekProgress.locking!.locked_talents.find(
                        lt => lt.talent_id === talent.talent_id && lt.tree_type === treeType
                      )
                    );
                    
                    if (treeTalents.length === 0) return null;

                    const totalTargetMinutes = treeTalents.reduce((sum, talent) => sum + talent.target_minutes, 0);
                    const totalCompletedMinutes = treeTalents.reduce((sum, talent) => sum + talent.completed_minutes, 0);
                    const completionPercentage = totalTargetMinutes > 0 ? (totalCompletedMinutes / totalTargetMinutes) * 100 : 0;

                    const treeConfigs = {
                      longevity: { name: 'Longevity & Lifespan', color: 'success.main', progressColor: 'success' },
                      healthspan: { name: 'Health Span', color: 'primary.main', progressColor: 'primary' },
                      wellness: { name: 'Wellness & Recovery', color: 'warning.main', progressColor: 'warning' }
                    } as const;
                    
                    const treeConfig = treeConfigs[treeType as keyof typeof treeConfigs];

                    return (
                      <Grid item xs={12} md={4} key={treeType}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ mb: 1, color: treeConfig.color }}>
                            {treeConfig.name}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(completionPercentage, 100)}
                            sx={{ height: 12, borderRadius: 6, mb: 1 }}
                            color={treeConfig.progressColor as any}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {Math.round(totalCompletedMinutes / 60)}/{Math.round(totalTargetMinutes / 60)} hours completed
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {treeTalents.length} talent{treeTalents.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </MotionCard>
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={showPaymentSuccess}
        autoHideDuration={6000}
        onClose={() => setShowPaymentSuccess(false)}
      >
        <Alert onClose={() => setShowPaymentSuccess(false)} severity="success">
          Payment successful! Thank you for upgrading to Premium!
        </Alert>
      </Snackbar>

      {/* Onboarding Tour */}
      <OnboardingTour
        open={showTour}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
      />
    </Container>
  );
};

export default Dashboard; 